import React, { useState, useCallback, useMemo, useEffect, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { useFormikContext } from "formik";
import { Box, Grid } from "@material-ui/core";
import { size, isUndefined } from 'lodash';
import ModalSmg from "../Modal";
import DialogContentSmg from "../DialogContentSmg";
import ReCAPTCHA from "react-google-recaptcha";
import ButtonSmg from "../ButtonSmg";

import { useRecaptcha, useLoadingIndicatorContext } from "../../hooks";

const Form = forwardRef(({
  children,
  action = "#",
  GridContainerProps = { spacing: 1, justifyContent: "center" },
  recaptcha = true,
  submitButton = true,
  extraDisabledButtonSubmit,
  SubmitButtonProps = {
    id: "btn-submit-form",
    content: "Enviar",
    color: "primary",
    fullWidth: true,
  },
  GridSubmitButtonProps,
  resetButton = false,
  ResetButtonProps = {
    id: "btn-reset-form",
    content: "Limpiar",
    color: "secondary",
    fullWidth: true,
  },
  GridResetButtonProps,
  onKeyDown: onKeyDownProps,
  forceEnabledSubmit=false,
  afterSubmit = null,
  ...props
}, ref) => {
  const {
    isSubmitting,
    dirty,
    submitForm,
    handleReset,
    setFieldValue,
    validateForm,
    setSubmitting,
    errors,
  } = useFormikContext();

  const { loading, setLoading } = useLoadingIndicatorContext();
  const [modalError, setModalError] = useState(false)

  const handleCloseModalError = () =>{
    setModalError(false)
  }

  const setChangeToken = useCallback(
    valueToken => {
      setFieldValue("recaptcha", valueToken);
      submitForm();
    },
    [setFieldValue, submitForm]
  );

  const { executeRecaptcha, resetRecaptcha, ReCaptchaProps } = useRecaptcha(
    setChangeToken
  );

  const handleRecaptcha = useCallback(
    () => {
      resetRecaptcha();
      executeRecaptcha();
    },
    [resetRecaptcha, executeRecaptcha],
  );

  const handleSubmitWithCaptcha = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
      
      if (recaptcha) {
        validateForm().then(combinedErrors => {
          if (Object.keys(combinedErrors).length === 0) {
            if(navigator.onLine){
              handleRecaptcha();
            }else {
              setModalError(true)
            }
          } else {
            submitForm();
          }
        });
      } else {
        submitForm();
      }
      if(navigator.onLine){
      setSubmitting(true)
      }else{}
    },
    [
      setSubmitting,
      recaptcha,
      validateForm,
      handleRecaptcha,
      submitForm
    ]
  );

  useEffect(() => {
    setLoading(isSubmitting);
  }, [setLoading, isSubmitting]);

  const isValid = useMemo(
    () => (
      dirty 
        && size(errors) === 0 
          && (!isUndefined(extraDisabledButtonSubmit) ? extraDisabledButtonSubmit : true)
    ),
    [dirty, errors, extraDisabledButtonSubmit]
  );

  const onKeyDown = keyEvent => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      resendForm: (e) => handleSubmitWithCaptcha(e),
      setFieldValue
    }),
    [handleSubmitWithCaptcha, setFieldValue],
  );

  return (
    <>
      <Box
        ref={ref}
        component="form"
        action={action}
        onSubmit={handleSubmitWithCaptcha}
        onReset={handleReset}
        onKeyDown={onKeyDownProps ? onKeyDownProps : onKeyDown}
        {...props}
      >
        <Grid container {...GridContainerProps}>
          {children}
          {recaptcha && <ReCAPTCHA {...ReCaptchaProps} />}
          {resetButton && (
            <Grid item {...GridResetButtonProps}>
              <ButtonSmg
                type="reset"
                disabled={!dirty || isSubmitting}
                {...ResetButtonProps}
              />
            </Grid>
          )}
          {submitButton && (
            <Grid item xs={12} {...GridSubmitButtonProps}>
              <ButtonSmg
                type="submit"
                disabled={forceEnabledSubmit?false:(!isValid || loading)}
                {...SubmitButtonProps}
              />
            </Grid>
          )}
          {afterSubmit}
          <ModalSmg
            id="offline-modal"
            open={modalError}
            ButtonsProps={[
              {
                id: "btn-confirm-modal-error",
                color: "primary",
                content: "Aceptar",
                onClick: handleCloseModalError
              }
            ]}
          >
            <DialogContentSmg title="¡Lo sentimos!" message="Tenemos inconvenientes al procesar el trámite, por favor intente nuevamente más tarde." />  
          </ModalSmg>
        </Grid>
      </Box>
    </>
  );
});

Form.displayName = 'Form';

Form.propTypes = {
  children: PropTypes.node.isRequired,
  action: PropTypes.string,
  GridContainerProps: PropTypes.object,
  recaptcha: PropTypes.bool,
  submitButton: PropTypes.bool,
  extraDisabledButtonSubmit: PropTypes.bool,
  SubmitButtonProps: PropTypes.object,
  GridSubmitButtonProps: PropTypes.object,
  resetButton: PropTypes.bool,
  ResetButtonProps: PropTypes.object,
  GridResetButtonProps: PropTypes.object,
  onKeyDown: PropTypes.func,
  afterSubmit: PropTypes.node,
};

export default Form;
